import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../translations/useTranslation';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Card,
  CardBody,
  Image,
  Stack,
  Badge,
  Button,
  Icon,
  Flex,
  Divider,
  useToast,
  Skeleton
} from '@chakra-ui/react';
import { FaStore, FaMapMarkerAlt, FaPhone, FaEnvelope, FaArrowRight } from 'react-icons/fa';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api_keys';
import FilterBar from '../../SearchComponents/FilterBar';

const StoreList = () => {
  const { t } = useTranslation();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [filters, setFilters] = useState({
    state: '',
    district: '',
    radius: ''
  });
  const toast = useToast();

  // Get user's location for distance filtering
  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
      },
      (error) => {
        console.error("Error getting user location:", error);
      }
    );
  }, []);

  // Fetch stores with filters
  useEffect(() => {
    const fetchStores = async () => {
      setLoading(true);
      try {
        let url = `${API_BASE_URL}/api/stores/verified`;
        
        // Add query parameters for filtering
        const params = new URLSearchParams();
        
        if (filters.state) {
          params.append('state', filters.state);
        }
        
        if (filters.district) {
          params.append('district', filters.district);
        }
        
        if (filters.radius && userLocation) {
          params.append('radius', filters.radius);
          params.append('lat', userLocation.lat);
          params.append('lng', userLocation.lng);
        }
        
        // Add params to URL if any exist
        if (params.toString()) {
          url += `?${params.toString()}`;
        }
        
        const response = await axios.get(url);
        setStores(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching stores:', err);
        setError('Failed to load stores. Please try again later.');
        setLoading(false);
        
        toast({
          title: 'Error',
          description: 'Failed to load stores. Please try again later.',
          status: 'error',
          duration: 5000,
          isClosable: true
        });
      }
    };

    fetchStores();
  }, [filters, userLocation, toast]);

  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  // Calculate distance between two coordinates in kilometers
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return null;
    
    const R = 6371; // Radius of the earth in km
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lon2 - lon1);
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * 
      Math.sin(dLon/2) * Math.sin(dLon/2); 
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
    const distance = R * c; // Distance in km
    return distance.toFixed(1);
  };

  const deg2rad = (deg) => {
    return deg * (Math.PI/180);
  };

  // Render loading skeletons
  const renderSkeletons = () => {
    return Array(4).fill(0).map((_, index) => (
      <Card key={index} maxW="100%" overflow="hidden" variant="outline">
        <Skeleton height="200px" />
        <CardBody>
          <Stack spacing={4}>
            <Skeleton height="20px" width="70%" />
            <Skeleton height="15px" width="90%" />
            <Skeleton height="15px" width="60%" />
            <Skeleton height="40px" width="120px" />
          </Stack>
        </CardBody>
      </Card>
    ));
  };

  return (
    <Container maxW="container.xl" py={8}>
      <Box textAlign="center" mb={8}>
        <Heading size="xl" mb={2} color="green.600">
          <Icon as={FaStore} mr={2} />
          {t('agriculturalStores')}
        </Heading>
        <Text color="gray.600">
          {t('findLocalStores')}
        </Text>
      </Box>

      {/* Filter Bar */}
      <Box mb={8}>
        <FilterBar onFilterChange={handleFilterChange} initialFilters={filters} />
      </Box>

      {/* Store Listings */}
      {loading ? (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {renderSkeletons()}
        </SimpleGrid>
      ) : error ? (
        <Box textAlign="center" p={8}>
          <Text color="red.500">{error}</Text>
          <Button mt={4} colorScheme="green" onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </Box>
      ) : stores.length === 0 ? (
        <Box textAlign="center" p={8}>
          <Text fontSize="lg">{t('noStoresFound')}</Text>
          <Text mt={2}>{t('tryAdjustingFilters')}</Text>
          <Button as={Link} to="/create-store" mt={4} colorScheme="green">
            {t('addYourStore')}
          </Button>
        </Box>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {stores.map((store) => {
            // Calculate distance if user location is available
            const distance = userLocation && store.location?.coordinates ? 
              calculateDistance(
                userLocation.lat, 
                userLocation.lng, 
                store.location.coordinates[1], // latitude is second in GeoJSON
                store.location.coordinates[0]  // longitude is first in GeoJSON
              ) : null;
              
            return (
              <Card key={store._id} maxW="100%" overflow="hidden" variant="outline" 
                _hover={{ boxShadow: 'md', transform: 'translateY(-2px)' }}
                transition="all 0.2s"
              >
                {store.coverImage ? (
                  <Image
                    src={store.coverImage}
                    alt={store.name}
                    height="200px"
                    objectFit="cover"
                  />
                ) : (
                  <Box height="200px" bg="gray.100" display="flex" alignItems="center" justifyContent="center">
                    <Icon as={FaStore} boxSize={12} color="gray.400" />
                  </Box>
                )}
                
                <CardBody>
                  <Flex align="center" mb={2}>
                    {store.logo && (
                      <Image 
                        src={store.logo} 
                        alt="logo" 
                        boxSize="50px" 
                        borderRadius="full" 
                        mr={3} 
                        objectFit="cover"
                        border="1px solid"
                        borderColor="gray.200"
                      />
                    )}
                    <Heading size="md">{store.name}</Heading>
                  </Flex>
                  
                  <Text noOfLines={2} mb={3} color="gray.600">
                    {store.description}
                  </Text>
                  
                  <Flex align="center" mb={2}>
                    <Icon as={FaMapMarkerAlt} color="green.500" mr={2} />
                    <Text fontSize="sm">
                      {store.address.city}, {store.address.state}
                      {distance && (
                        <Badge ml={2} colorScheme="green">{distance} {t('kmAway')}</Badge>
                      )}
                    </Text>
                  </Flex>
                  
                  {store.contactPhone && (
                    <Flex align="center" mb={2}>
                      <Icon as={FaPhone} color="green.500" mr={2} />
                      <Text fontSize="sm">{store.contactPhone}</Text>
                    </Flex>
                  )}
                  
                  <Divider my={3} />
                  
                  <Button 
                    as={Link} 
                    to={`/store/${store._id}`}
                    colorScheme="green" 
                    variant="outline"
                    rightIcon={<FaArrowRight />}
                    size="sm"
                    width="100%"
                  >
                    {t('viewStore')}
                  </Button>
                </CardBody>
              </Card>
            );
          })}
        </SimpleGrid>
      )}
      
      {/* Add Store Button */}
      <Flex justify="center" mt={10}>
        <Button 
          as={Link} 
          to="/create-store" 
          colorScheme="green" 
          size="lg"
          leftIcon={<FaStore />}
        >
          {t('addYourStore')}
        </Button>
      </Flex>
    </Container>
  );
};

export default StoreList;